// API del módulo «Ventas» (TKT-00223). Backend (mcp_django · apps/invoicing/ventas.py):
//   GET /api/v1/admin/invoicing/ventas?desde&hasta&estado&origen&evento   (cobros:read)
//   GET /api/v1/admin/invoicing/ventas.csv?…mismos filtros…                (cobros:read)
// La org sale SIEMPRE del JWT (nunca la mandamos). Montos en centavos y TODOS los
// totales vienen calculados del servidor: esta pantalla no suma dinero.
// Cada venta sigue el contrato `FinancialEvent` v0 (RFC Finance OS §F0).

import { ensureFreshToken } from '@/lib/tokenRefresh';

const DJANGO_PROXY = '/django-api';
const BASE = '/api/v1/admin/invoicing/ventas';

export type Origen = 'evento' | 'reserva' | 'expo';
export type Estado = 'pagado' | 'reembolsado' | 'pendiente';
export type FacturaEstado = 'timbrada' | 'pendiente' | 'por_aprobar' | 'error' | 'cancelada' | 'no_solicitada';

export interface Factura {
  solicitada: boolean;
  datos_fiscales: boolean;
  rfc: string | null;
  razon_social: string | null;
  cfdi_id: string | null;
  cfdi_uuid: string | null;
  motivo: string | null;
  estado: FacturaEstado;
  estado_label: string;
}

export interface Venta {
  // Contrato FinancialEvent v0
  org_id: string;
  source: string;
  source_ref: string;
  external_ref: string | null;
  kind: 'charge_succeeded' | 'refund' | 'charge_pending';
  status: 'paid' | 'refunded' | 'pending';
  occurred_at: string | null;
  amount_cents: number;
  currency: string;
  counterparty_label: string | null;
  // Detalle para mostrar
  id: string;
  origen: Origen;
  origen_label: string;
  referencia_id: string;
  concepto: string;
  detalle: string;
  evento_slug: string | null;
  cantidad: number | null;
  estado: Estado;
  estado_label: string;
  pagado_en: string | null;
  creado_en: string | null;
  monto_lista_cents: number;
  metodo: string | null;
  metodo_label: string;
  codigo_descuento: string | null;
  comprador: { nombre: string | null; correo: string | null; empresa: string | null };
  factura: Factura;
  factura_pendiente: boolean;
  stripe: { payment_intent: string | null; session_id: string | null; modo: 'live' | 'test' | null; url: string | null };
}

export interface TotalMoneda {
  currency: string;
  cobrado_cents: number;
  ventas_pagadas: number;
  lugares_pagados: number;
  reembolsado_cents: number;
  ventas_reembolsadas: number;
  pendiente_cents: number;
  ventas_pendientes: number;
  ticket_promedio_cents: number | null;
  por_origen: { origen: Origen; origen_label: string; cobrado_cents: number; ventas: number }[];
  por_periodo: { periodo: string; cobrado_cents: number; ventas: number }[];
}

export interface VentasResp {
  org_id: string;
  periodo: { desde: string; hasta: string; zona: string };
  filtros: { estado: string; origen: string; evento: string | null };
  totales: {
    agrupado_por: 'dia' | 'mes';
    por_moneda: TotalMoneda[];
    facturas_pendientes: number;
    modo_prueba: { ventas: number; amount_cents: number };
  };
  emisor_fiscal: { configurado: boolean; listo: boolean };
  total_ventas: number;
  mostradas: number;
  truncado: boolean;
  duplicados_omitidos: number;
  ventas: Venta[];
}

export interface FiltrosVentas {
  desde: string;
  hasta: string;
  estado?: Estado | '';
  origen?: Origen | '';
}

async function jwt(): Promise<string> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  if (!token) throw new Error('Tu sesión expiró. Inicia sesión otra vez.');
  return token;
}

function qs(f: FiltrosVentas & { limite?: number }): string {
  const q = new URLSearchParams();
  q.set('desde', f.desde);
  q.set('hasta', f.hasta);
  if (f.estado) q.set('estado', f.estado);
  if (f.origen) q.set('origen', f.origen);
  if (f.limite) q.set('limite', String(f.limite));
  return `?${q.toString()}`;
}

async function errorDe(res: Response): Promise<Error> {
  if (res.status === 403) return new Error('Solo los administradores de la organización pueden ver las ventas.');
  const body = await res.json().catch(() => ({}));
  const detail = (body as { detail?: unknown }).detail || `Error ${res.status} del servidor`;
  return new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
}

export async function fetchVentas(f: FiltrosVentas): Promise<VentasResp> {
  const res = await fetch(`${DJANGO_PROXY}${BASE}${qs({ ...f, limite: 500 })}`, {
    headers: { Authorization: `Bearer ${await jwt()}`, Accept: 'application/json' },
  });
  if (!res.ok) throw await errorDe(res);
  return res.json() as Promise<VentasResp>;
}

/** Descarga el CSV con los mismos filtros (con auth: no basta un <a href>). */
export async function descargarCsv(f: FiltrosVentas): Promise<void> {
  const res = await fetch(`${DJANGO_PROXY}${BASE}.csv${qs(f)}`, {
    headers: { Authorization: `Bearer ${await jwt()}` },
  });
  if (!res.ok) throw await errorDe(res);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `ventas-${f.desde}-a-${f.hasta}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
